import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CButton, CCard, CCardBody, CCardHeader, CCardFooter, CBadge, CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { CSmartPagination, CSmartTable } from '@coreui/react-pro';
import { ADMIN_FILTER, DEFAULT_DATE_FORMAT, DEFAULT_DATE_TIME_FORMAT, LLM_STATUS } from '../../../../constants';
import { formatDate, formatDateTimeZone, toastError } from '../../../../utils';
import { callTokenApi } from '../../../../apiCaller';
import { Link } from 'react-router-dom';
import ModalImportCSV from './ModalImportCSV';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { cilBriefcase } from '@coreui/icons';
import CFilter from './CFilter';
import ModalShowField from './ModalShowField';
import SelectAccounts from './SelectAccounts';
import { DEFAULT_FIELDS, LIST_FIELDS } from './constants';
import cloneDeep from 'lodash/cloneDeep';
import TooltipContentTemplate from '../../subscriber/analytics/report-v2/TooltipContentTemplate';
import { Tooltip } from '@progress/kendo-react-tooltip';
import ScriptEditor from './ScriptEditor';
import EnrichmentData from './EnrichmentData';
import { enrichType } from '../accounts/EnrichmentRule/components/Ultils';

const MAIN_TABLE_NAME = 'CentralizedPerson';
const DEFAULT_SORT_ORDER = '-created_at';

export const getEnrichmentTypeColor = (type) => {
     switch (type?.toLowerCase()) {
          case 'b2b':
               return 'primary';
          case 'b2c':
               return 'info';
          case 'none':
               return 'dark';
          default:
               return 'success';
     }
};

export const getBadgeColor = (type) => {
     switch (type) {
          case LLM_STATUS.GOOD:
               return 'success';
          case LLM_STATUS.BAD:
               return 'danger';
          case LLM_STATUS.NONE:
               return 'dark';
          default:
               return 'primary';
     }
};

const CentralizedPerson = () => {
     const [showImportModal, setShowImportModal] = useState(false);
     const [showSettingsModal, setShowSettingsModal] = useState(false);
     const [showFilters, setShowFilters] = useState(false);
     const [itemPerPage, setItemPerPage] = useState(10);
     const [fields, setFields] = useState(cloneDeep(LIST_FIELDS))
     const [filterAccount, setFilterAccount] = useState('')

     const [centralizedPerson, setCentralizedPerson] = useState([]);
     const [isLoading, setIsLoading] = useState(true);
     const [activePage, setActivePage] = useState(1);
     const [totalPages, setTotalPages] = useState(1);
     const [orderFieldId, setOrderFieldId] = useState(DEFAULT_SORT_ORDER);
     const [currentFilters, setCurrentFilters] = useState([{ key: '', condition: 'ct', type: '', value: '' }]);

     const [positionTooltip, setPositionTooltip] = useState('right');
     const [showCopyTooltip, setShowCopyTooltip] = useState(true);
     const [openTooltip, setOpenTooltip] = useState(false);
     const [showShortId, setShowShortId] = useState([]);
     const [targetElement, setTargetElement] = useState(null);
     const [rectCell, setRectCell] = useState({});
     const [isReloading, setIsReloading] = useState(false);
     const refCell = useRef(null);

     const settingProps = {
          itemPerPage,
          setItemPerPage,
          fields,
          setFields,
          listFields: LIST_FIELDS,
          defaultField: DEFAULT_FIELDS
     }

     const fetchCentralizedPerson = useCallback(async (page = 1, currentOrderFieldId = orderFieldId, filtersToUse = currentFilters) => {
          setIsLoading(true);
          const skip = (page - 1) * itemPerPage;
          let filterQuery = '';

          for (const filterKey in filtersToUse) {
               const { key, value, condition, fieldType, tableName } = filtersToUse[filterKey];
               const isEmptyCondition = condition === 'empty' || condition === 'notEmpty';
               if (key && condition && (value || isEmptyCondition)) {
                    const dateValue = typeof value?.getMonth === 'function' ? formatDate(value, DEFAULT_DATE_FORMAT) : '';
                    filterQuery += `&filter.${tableName || MAIN_TABLE_NAME}.${key}.${condition}${fieldType ? `.${fieldType}` : ''}=${isEmptyCondition ? '' : (dateValue || value)}`;
               }
          }

          if (filterAccount) {
               filterQuery += `&filter.${MAIN_TABLE_NAME}.accountId.eq=${filterAccount}`;
          }

          let urlToCall = `${ADMIN_FILTER}?tableName=${MAIN_TABLE_NAME}&skip=${skip}&limit=${itemPerPage}${filterQuery}`;
          if (currentOrderFieldId) {
               urlToCall += `&orderFieldId=${currentOrderFieldId}`;
          }

          try {
               const response = await callTokenApi(urlToCall, 'GET', null);
               if (response.status === 200) {
                    const { total, res } = response.data;
                    const calculatedTotalPages = Math.ceil(total / itemPerPage);

                    setCentralizedPerson(res);
                    setTotalPages(calculatedTotalPages > 0 ? calculatedTotalPages : 1);
                    setActivePage(page);
               } else {
                    toastError(response);
                    setTotalPages(1);
                    setActivePage(1);
               }
          } catch (error) {
               console.error("API call failed:", error);
               toastError("An unexpected error occurred while fetching data.");
               setTotalPages(1);
               setActivePage(1);
          } finally {
               setIsLoading(false);
          }
     }, [orderFieldId, filterAccount, currentFilters, itemPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

     useEffect(() => {
          fetchCentralizedPerson(1, orderFieldId, currentFilters);
     }, [fetchCentralizedPerson, orderFieldId, filterAccount, itemPerPage, currentFilters]); // eslint-disable-line react-hooks/exhaustive-deps

     const handlePaginationChange = (newPage) => {
          if (newPage !== activePage) {
               fetchCentralizedPerson(newPage, orderFieldId, currentFilters);
          }
     };

     const handleSortChange = (sorter) => {
          let newSortOrder = DEFAULT_SORT_ORDER;

          if (sorter.state === 'asc') {
               newSortOrder = `${sorter.column}`;
          } else if (sorter.state === 'desc') {
               newSortOrder = `-${sorter.column}`;
          }

          if (newSortOrder !== orderFieldId) {
               setOrderFieldId(newSortOrder);
          }
     };

     const handleReload = async () => {
          setIsReloading(true);
          try {
               await fetchCentralizedPerson(activePage, orderFieldId, currentFilters);
          } finally {
               // Add a minimum delay to show the animation
               setTimeout(() => {
                    setIsReloading(false);
               }, 800);
          }
     };

     const handleShowShortId = useCallback((e, field) => {
          let newShowShortId = [...showShortId];

          if (showShortId.includes(field)) {
               newShowShortId = [...showShortId].filter((item) => item !== field);
          } else {
               newShowShortId.push(field);
          }

          e.stopPropagation();
          setShowShortId(newShowShortId);
     }, [showShortId]);

     useEffect(() => {
          const handleOutsideClick = (event) => {
               if (event.target.attributes.getNamedItem('title') === null && event.target.getAttribute('class') !== 'icon-copy') {
                    setOpenTooltip(false);
               }
          };

          const hideTooltip = () => {
               setOpenTooltip(false);
          };

          document.addEventListener('click', handleOutsideClick);
          document.addEventListener('scroll', hideTooltip);

          return () => {
               document.removeEventListener('click', handleOutsideClick);
               document.removeEventListener('scroll', hideTooltip);
          };
     }, []);

     const changePositionTooltip = (props) => {
          let position = 'right';
          let left = rectCell.left * 1.25 + 10;
          let top = rectCell.top - props.element.clientHeight / 4;

          setPositionTooltip(position);
          return { left, top };
     };

     const showTooltip = (element, isHiddenCopy) => {
          isHiddenCopy ? setShowCopyTooltip(false) : setShowCopyTooltip(true);
          setOpenTooltip(true);
          setTargetElement(element);
     };

     const tableColumns = useMemo(() => {
          const cloneFields = cloneDeep(fields)
          return cloneFields.map(field => {
               if (field.key === 'accountId') {
                    field.label = (
                         <div className='d-inline'>
                              <strong className='d-inline-flex align-items-center gap-3'>
                                   <span className='mt-1'>Account ID</span>
                                   <div
                                        className='pointer'
                                        onClick={(e) => {
                                             handleShowShortId(e, field.key);
                                        }}
                                   >
                                        <CIcon icon={showShortId.includes(field.key) ? 'iconEyeRegular' : 'iconEyeRegularSlash'}></CIcon>
                                   </div>
                              </strong>
                         </div>
                    )
               }
               return field
          })
     }, [fields, showShortId, handleShowShortId]); // eslint-disable-line

     return (
          <>
               <CCard className='ui__table'>
                    <CCardHeader>
                         <div className='d-flex justify-content-between align-items-center border-bottom-0'>
                              <h3 className='ui__table--header mb-0'>Centralized Person</h3>

                              <div className='ui__table--toolbar'>
                                   <Tippy content='Reload Data' placement='bottom'>
                                        <CButton
                                             variant="outline"
                                             color="success"
                                             size="sm"
                                             className="rounded-lg px-2 py-1 mr-2"
                                             onClick={handleReload}
                                             disabled={isReloading}
                                             style={{
                                                  minWidth: '36px',
                                                  transition: 'all 0.3s ease',
                                                  position: 'relative',
                                                  transform: 'unset'
                                             }}
                                        >
                                             <CIcon
                                                  icon="cil-reload"
                                                  style={{
                                                       transform: isReloading ? 'rotate(360deg)' : 'rotate(0deg)',
                                                       transition: 'transform 0.8s ease-in-out',
                                                       display: 'inline-block'
                                                  }}
                                             />
                                        </CButton>
                                   </Tippy>

                                   <CButton variant="outline" className="rounded-lg px-2 py-1" color='primary mr-2' onClick={() => setShowImportModal(true)}>
                                        Import CSV
                                   </CButton>

                                   <Tippy content={showFilters ? 'Hide Filters' : 'Show Filters'} placement='bottom'>
                                        <CButton variant="outline" color="light" size="sm" className="rounded-lg px-2 py-1" onClick={() => setShowFilters(prev => !prev)}>
                                             <span className='ui__table--toolbar-btn-text'>{showFilters ? 'Hide' : 'Show'} Filters</span>
                                        </CButton>
                                   </Tippy>
                                   <CButton variant="outline" className="rounded-lg px-2 py-1" color='primary' onClick={() => setShowSettingsModal(true)}>
                                        <CIcon icon="cil-settings" />
                                   </CButton>
                              </div>
                         </div>
                         <SelectAccounts filterAccount={filterAccount} setFilterAccount={setFilterAccount} />
                         {showFilters && (
                              <CFilter
                                   currentFilters={currentFilters}
                                   setCurrentFilters={setCurrentFilters}
                                   listFields={LIST_FIELDS}
                              />
                         )}
                    </CCardHeader>

                    <CCardBody style={{
                         marginTop: '30px',
                         border: '1px solid #f0f3f5',
                         borderRadius: '23px',
                    }}>

                         <Tooltip
                              onPosition={changePositionTooltip}
                              tooltipStyle={{ background: '#050505' }}
                              content={(props) => <TooltipContentTemplate props={props} showCopyTooltip={showCopyTooltip} maxWidth='400px' />}
                              openDelay={0}
                              open={openTooltip}
                              targetElement={targetElement}
                              position={positionTooltip}
                         >
                              <CSmartTable
                                   items={centralizedPerson}
                                   columns={tableColumns}
                                   itemsPerPage={itemPerPage}
                                   tableProps={{
                                        hover: true,
                                        responsive: true,
                                        className: 'mb-0',
                                   }}
                                   tableHeadProps={{
                                        className: 'ui__table--data-header',
                                   }}
                                   tableBodyProps={{
                                        className: 'ui__table--data-content',
                                   }}
                                   columnSorter={{ external: true, resetable: true }}
                                   sorterValue={orderFieldId ? {
                                        column: orderFieldId.startsWith('-') ? orderFieldId.substring(1) : orderFieldId,
                                        state: orderFieldId.startsWith('-') ? 'desc' : 'asc',
                                   } : {}}
                                   onSorterChange={handleSortChange}
                                   loading={isLoading}
                                   scopedColumns={{
                                        accountId: (item) => (
                                             <td key={'accountId'} style={{ textAlign: 'center', cursor: 'pointer' }} ref={refCell}
                                                  onClick={
                                                       item.accountId !== 'undefine' && !showShortId.includes('accountId')
                                                            ? (event) => {

                                                                 showTooltip(event.currentTarget);
                                                                 setRectCell(event.currentTarget.getBoundingClientRect());
                                                            }
                                                            : null
                                                  }
                                                  tabIndex={0}
                                                  data-keyboardnavid={item.accountId}
                                                  title={' '}
                                                  data-title={item.accountId}>{
                                                       <>
                                                            {showShortId.includes('accountId') ? (
                                                                 <>{item.accountId}</>
                                                            ) : (
                                                                 <i style={{ pointerEvents: 'none' }}>
                                                                      <CIcon icon='iconFingerprint' className='icon-action icon-trigger icon-trigger-lookup' style={{ color: '#63c2de' }} />
                                                                 </i>
                                                            )}
                                                       </>
                                                  }</td>
                                        ),
                                        firstName: (item) => (<td>{item.firstName || '--'}</td>),
                                        lastName: (item) => (<td>{item.lastName || '--'}</td>),
                                        currentTitle: (item) => (<td>{item.currentTitle || '--'}</td>),
                                        currentCompany: (item) => (<td>{item.currentCompany || '--'}</td>),
                                        primaryEmail: (item) => (
                                             <td>
                                                  <CButton size="sm" role="button" className="px-0 py-0 text-lowercase d-flex align-items-start">
                                                       <span className='ms-1'>{item.primaryEmail || '--'}</span>
                                                  </CButton>
                                             </td>
                                        ),
                                        linkedinURL: (item) => (
                                             <td>
                                                  {item.linkedinURL ? (
                                                       <Tippy content={item.linkedinURL} placement='bottom'>
                                                            <CButton size="sm" role="button" target="_blank" rel="noopener noreferrer" className="px-0 py-0 position-relative">
                                                                 <CIcon icon={cilBriefcase} size="xl" className="text-dark m-0" />
                                                            </CButton>
                                                       </Tippy>
                                                  ) : '--'}
                                             </td>
                                        ),
                                        sourcePath: (item) => (
                                             <td>
                                                  {item.sourcePath ? (
                                                       <CBadge textBgColor="light">{item.sourcePath}</CBadge>
                                                  ) : ('--')}
                                             </td>
                                        ),
                                        enrichType: (item) => (
                                             <td>
                                                  {item.sourcePath && item.sourcePath.includes('revealed') ? enrichType(item) : '--'}
                                             </td>
                                        ),
                                        LLMstatus: (item) => (
                                             <td>
                                                  {item.LLMstatus ? (
                                                       <Tippy content={item.LLMstatus} placement='bottom'>
                                                            <CBadge textBgColor={getBadgeColor(item.LLMstatus)} className="text-white d-flex align-items-center">
                                                                 <span className='ms-1'>{item.LLMstatus}</span>
                                                            </CBadge>
                                                       </Tippy>
                                                  ) : '--'}
                                             </td>
                                        ),
                                        created_at: (item) => <td>{formatDateTimeZone(item.created_at, DEFAULT_DATE_TIME_FORMAT, item.timeZone)}</td>,
                                        updated_at: (item) => <td>{formatDateTimeZone(item.updated_at, DEFAULT_DATE_TIME_FORMAT, item.timeZone)}</td>,
                                        id: (item) => (
                                             <td>
                                                  <Link to={`/centralized-person/${item.id}`}>
                                                       {item.id}
                                                  </Link>
                                             </td>
                                        ),
                                        viewDetails: (item) => (
                                             <td className="text-center py-2">
                                                  <Tippy content='View Detail' placement='bottom'>
                                                       <Link to={`/centralized-person/${item.id}`}>
                                                            <CButton
                                                                 color="primary"
                                                                 variant="ghost"
                                                                 size="sm"
                                                            >
                                                                 <CIcon icon='iconEye' size="sm" className="m-0" />
                                                            </CButton>
                                                       </Link>
                                                  </Tippy>
                                             </td>
                                        ),
                                   }}
                              />
                         </Tooltip>
                    </CCardBody>

                    <CCardFooter className="mt-3 py-4">
                         <CSmartPagination
                              activePage={activePage}
                              pages={totalPages}
                              onActivePageChange={handlePaginationChange}
                         />
                    </CCardFooter>
               </CCard>
               <CAccordion className="mt-4">
                    <CAccordionItem itemKey="script-editor">
                         <CAccordionHeader>
                              <strong>Testing AD-Graph</strong>
                         </CAccordionHeader>
                         <CAccordionBody>
                              <ScriptEditor />
                         </CAccordionBody>
                    </CAccordionItem>
                    <CAccordionItem itemKey="enrichment-data">
                         <CAccordionHeader>
                              <strong>Enrichment B2B Data Management</strong>
                         </CAccordionHeader>
                         <CAccordionBody>
                              <EnrichmentData />
                         </CAccordionBody>
                    </CAccordionItem>
               </CAccordion>
               <ModalImportCSV visible={showImportModal} onClose={() => setShowImportModal(false)} />
               <ModalShowField visible={showSettingsModal} onClose={() => setShowSettingsModal(false)} {...settingProps} />
          </>
     );
};

export default CentralizedPerson;
